import { useDataAttributes } from '@data-attributes/use-data-attributes';
import { Link } from '@eika/link';
import { useAccountApplicationActions } from '@hooks/context/use-account-application-context';
import { useLinks } from '@hooks/use-links';
import { format } from '@translation-utils/use-translation';
import { useTranslation } from '@translations';
import { alreadyHasAccount, getExistingProcessId } from '@utils/child-status-utils';
import { useHashRoute } from '@utils/navigation-utils';
import { useNavigate } from 'react-router-dom';
import { useChildCardContext } from '../context';
import {
    canCompleteKYCForm,
    canGiveConsent,
    canSeeCompleteProcessStatus,
    canSeeProcessStatus,
    canStartNewProcess,
} from '../utils';

export const CreateNewAskProcessButton = () => {
    const { childEligibility } = useChildCardContext();
    const { childCard } = useTranslation();
    const { childrenListDataAttributes } = useDataAttributes();
    const { enterApproverParentInformation } = useLinks();
    const navigate = useNavigate();
    const { setSelectedChildEligibility } = useAccountApplicationActions();

    const onClickCreateAsk = () => {
        setSelectedChildEligibility(childEligibility);
        navigate(enterApproverParentInformation.url);
    };

    if (canStartNewProcess(childEligibility)) {
        return (
            <Link
                aria-label={format(childCard.createAskFor, childEligibility.child.name)}
                linkType="button-positive"
                onClick={onClickCreateAsk}
                {...childrenListDataAttributes.createAsk}
            >
                {childCard.createAsk}
            </Link>
        );
    }

    return null;
};

export const GoToProcessStatusButton = () => {
    const { childEligibility } = useChildCardContext();
    const { childCard } = useTranslation();
    const { childrenListDataAttributes } = useDataAttributes();
    const { process } = useLinks();

    if (canSeeProcessStatus(childEligibility)) {
        const existingProcessId = getExistingProcessId(childEligibility);

        return (
            <Link
                aria-label={format(childCard.ariaLabel.goToStatusFor, childEligibility.child.name)}
                href={useHashRoute(`${process.url}/${existingProcessId}`)}
                linkType="button-negative"
                {...childrenListDataAttributes.goToStatus}
            >
                {childCard.goToStatus}
            </Link>
        );
    }

    return null;
};

export const GoToCompleteProcessStatusButton = () => {
    const { childEligibility } = useChildCardContext();
    const { childCard } = useTranslation();
    const { childrenListDataAttributes } = useDataAttributes();
    const { process } = useLinks();

    if (canSeeCompleteProcessStatus(childEligibility)) {
        const existingProcessId = getExistingProcessId(childEligibility);

        return (
            <Link
                aria-label={format(childCard.ariaLabel.goToCompleteStatusFor, childEligibility.child.name)}
                href={useHashRoute(`${process.url}/${existingProcessId}`)}
                {...childrenListDataAttributes.goToCompleteStatus}
            >
                {childCard.goToCompleteStatus}
            </Link>
        );
    }

    return null;
};

export const StartSavingButton = () => {
    const { childEligibility } = useChildCardContext();
    const { smartspar, childCard } = useTranslation();
    const { childrenListDataAttributes } = useDataAttributes();
    const links = useLinks();

    if (alreadyHasAccount(childEligibility)) {
        return (
            <Link
                aria-label={format(childCard.ariaLabel.startSavingFor, childEligibility.child.name)}
                href={links.smartspar.url}
                linkType="button-positive"
                {...childrenListDataAttributes.startSaving}
            >
                {smartspar.startSaving}
            </Link>
        );
    }

    return null;
};

export const GiveConsentButton = () => {
    const { childEligibility } = useChildCardContext();
    const { childCard } = useTranslation();
    const { childrenListDataAttributes } = useDataAttributes();
    const { process } = useLinks();

    if (canGiveConsent(childEligibility)) {
        const existingProcessId = getExistingProcessId(childEligibility);

        return (
            <Link
                aria-label={format(childCard.ariaLabel.giveConsentFor, childEligibility.child.name)}
                href={useHashRoute(`${process.url}/${existingProcessId}/samtykke`)}
                linkType="button-positive"
                {...childrenListDataAttributes.giveConsent}
            >
                {childCard.giveConsent}
            </Link>
        );
    }

    return null;
};

export const CompleteKYCButton = () => {
    const { childEligibility } = useChildCardContext();
    const { childCard } = useTranslation();
    const { childrenListDataAttributes } = useDataAttributes();
    const { nextStep } = useLinks();

    if (canCompleteKYCForm(childEligibility)) {
        const existingProcessId = getExistingProcessId(childEligibility);

        return (
            <Link
                aria-label={format(childCard.ariaLabel.completeKYCFormFor, childEligibility.child.name)}
                href={useHashRoute(`${nextStep.url}/${existingProcessId}`)}
                linkType="button-positive"
                {...childrenListDataAttributes.completeKYCForm}
            >
                {childCard.completeKYCForm}
            </Link>
        );
    }

    return null;
};
